import React from "react";
import { Box, Grid, Typography, Paper } from "@mui/material";
import {
  LocalShipping,
  DirectionsCar,
  LocationCitySharp,
} from "@mui/icons-material";
import { twMerge } from "tailwind-merge";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import ShipmentLineChart from "./ShipmentLineChart";
import RoadClosureMap from "./RoadClosureMap";
import { useNavigate } from "react-router-dom";
import ShipmentPriceChart from "./ShipmentPriceLineChart";
import ShipmentOperationsChart from "./ShipmentOperationsChart";
import { useAuth } from "hooks";
import MyFollowUpsWidget from "screens/App/CRM/Dashboard/MyFollowUpsWidget";
import { useLazyGetMyFollowUpsQuery } from "store/services/vendorLeadApi";
import { useLazyGetMyVendorLeadTasksQuery } from "store/services/noteApi";
import MyVendorLeadTasksWidget from "screens/App/CRM/Dashboard/MyVendorLeadTasksWidget";

export const AnalyticOverview = ({
  statsData,
  isLoading,
}) => {
  const navigate = useNavigate();
  const loggedInUser = useAuth();
  const userForVendorLead = loggedInUser?.loggedInUser?._id

  const [fetchMyFollowUps, { data: followUps, isLoading: isFollowUpsLoading }] =
    useLazyGetMyFollowUpsQuery();

  React.useEffect(() => {
    if (userForVendorLead) fetchMyFollowUps();
  }, [userForVendorLead, fetchMyFollowUps]);

  const hasFollowUps = isFollowUpsLoading || (followUps?.length ?? 0) > 0;

  const [fetchMyTasks, { data: myTasks, isLoading: isMyTasksLoading }] =
    useLazyGetMyVendorLeadTasksQuery();

  React.useEffect(() => {
    if (userForVendorLead) fetchMyTasks();
  }, [userForVendorLead, fetchMyTasks]);

  const hasMyTasks = isMyTasksLoading || (myTasks?.length ?? 0) > 0;

  const stats = [
    {
      title: "Shipment",
      icon: <LocalShipping fontSize="large" className="text-blue-500" />,
      items: [
        {
          label: "Total",
          count: statsData?.totalShipment ?? 0,
          link: "/app/crm/shipment/list",
        },
        {
          label: "Delivered",
          count: statsData?.shipmentDeliveredCount ?? 0,
          link: "/app/crm/shipment/list?shipmentStatus=Delivered",
        },
      ],
      bgColor: "bg-blue-50 dark:bg-blue-900",
    },
    {
      title: "Shipment",
      icon: <LocalShipping fontSize="large" className="text-green-500" />,
      items: [
        {
          label: "Pending ",
          count: statsData?.shipmentPendingCount ?? 0,
          link: "/app/crm/shipment/list?shipmentStatus=Pending",
        },
        {
          label: "Return ",
          count: statsData?.shipmentReturnCount ?? 0,
          link: "/app/crm/shipment/list?shipmentStatus=IsReturning",
        },
      ],
      bgColor: "bg-green-50 dark:bg-green-900",
    },
    {
      title: "Transport",
      icon: <DirectionsCar fontSize="large" className="text-yellow-500" />,
      items: [
        {
          label: "Total Vehicles",
          count: statsData?.vehicleCount ?? 0,
          link: "/app/crm/vehicle/list",
        },
        {
          label: "Total Drivers",
          count: statsData?.driverCount ?? 0,
          link: "/app/crm/driver/list",
        },
      ],
      bgColor: "bg-yellow-50 dark:bg-yellow-900",
    },
    {
      title: "Branch",
      icon: <LocationCitySharp fontSize="large" className="text-red-500" />,
      items: [
        {
          label: "Total",
          count: statsData?.totalBranch ?? 0,
          link: "/app/crm/branch/list",
        },
        {
          label: "Total warehouse",
          count: statsData?.totalWarehouse ?? 0,
          link: "/app/crm/warehouse/list",
        },
      ],
      bgColor: "bg-red-50 dark:bg-red-900",
    },
  ];

  if (isLoading) return <InfoGridSkeleton />;

  return (
    <Box>
      <Grid container spacing={2}>
        {stats.map((stat) => (
          <Grid
            key={stat.title}
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": { transform: "scale(1.02)" },
            }}
          >
            <Paper
              className={twMerge(
                "overflow-hidden p-2 shadow-md transition-all duration-300",
                stat.bgColor
              )}
              sx={{ borderRadius: 5, p: 2 }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  className="text-gray-900 dark:text-white"
                >
                  {stat.title}
                </Typography>
                {stat.icon}
              </Box>
              <Box>
                {stat.items.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    p={1}
                    className="dark:hover:bg-gray-1000 cursor-pointer rounded-lg hover:bg-gray-200 hover:bg-opacity-50"
                    onClick={() => navigate(item.link)}
                  >
                    <Typography variant="body1" color="textSecondary">
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      className="text-gray-900 dark:text-white"
                    >
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6} sx={{ minWidth: 0 }}>
          <ShipmentLineChart />
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ minWidth: 0 }}>
          <ShipmentPriceChart />
        </Grid>
        {hasFollowUps && (
          <Grid item xs={12} sm={6} md={6}>
            <MyFollowUpsWidget followUps={followUps ?? []} isLoading={isFollowUpsLoading} />
          </Grid>
        )}

        {hasMyTasks && (
          <Grid item xs={12} sm={6} md={6}>
            <MyVendorLeadTasksWidget tasks={myTasks ?? []} isLoading={isMyTasksLoading} />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={6} sx={{ minWidth: 0 }}>
          <ShipmentOperationsChart />
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ minWidth: 0 }}>
          <RoadClosureMap />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticOverview;
